/* eslint-disable react/no-array-index-key, consistent-return, sonarjs/cognitive-complexity, sonarjs/no-duplicate-string, no-throw-literal */
import { createAsyncThunk, createSlice, PayloadAction } from '@reduxjs/toolkit'
import { callErrorMsg } from 'helpers/errorMsg'
import type { CategoryType } from 'pages/PWP/Form/helper'
import type {
  PWPListItemType,
  PWPDetailType,
  PWPListProductType,
  PWPListParamsType,
  PWPProductListParamsType,
  PWPTargetedUserItemType,
  PWPPrioritySearchProductType,
  PWPSkpParamsType,
  PWPRuleDetailType,
} from 'utils/apiList/promoPwp'
import {
  getPwpData,
  getPwpType,
  getPwpProduct,
  postPwpCreate,
  putPwpEdit,
  getPwpDetail,
  putPwpEditStatus,
  postPwpBulkUpload,
  getPwpCommercialInternalSkp,
} from 'utils/apiList/promoPwp'
import { toastSuccess, toastFailed } from 'utils/toast'
import {
  fetchTargetedUser,
  removeTargetedUser,
  fetchSearchPWPProductTargeted,
  fetchPWPPriority,
  updatePWPPriority,
} from 'redux/pwp'

export const enumPwpRules = {
  CUSTOMER_BARU: 'CUSTOMER_BARU',
  MINIMUM_SPEND: 'MINIMUM_SPEND',
}

export const enumProductListType = {
  SKU_RELATED: 'skuRelated',
  PRODUCTS: 'products',
}

export const enumProductOnHubStatus = {
  ACTIVE: 'ACTIVE',
  INACTIVE: 'INACTIVE',
}

interface PWPDropdownType {
  checked: boolean
  childs: unknown[]
  key: string
  name: string
}

export interface PWPProductQuotaType {
  locationId: number
  locationName: string
  quotaCampaign: number
  quotaPerDay: number
  stock: number
  status: string
  price: number
}

export interface PWPProductType {
  productId: number
  productName: string
  sku: string
  discount: number
  limit: number
  pwpProductQuota: PWPProductQuotaType[]
  [key: string]: unknown
}

interface PWPRuleType {
  key: string
  items: CategoryType[]
  is_exclude: boolean
  isChecked: boolean
}

interface PWPSkpType {
  id: { id: number; name: string }
  name: string
}

export interface PWPFormType {
  pwpName: string
  startDate: string
  startTime: string
  endDate: string
  endTime: string
  isAllDay: boolean
  limitCampaign: number
  limitPerDay: number
  minimumSpend: number
  description: string
  skuRelated: (string | number)[]
  products: PWPProductType[]
  productForCreatePwp: PWPProductType[]
  rules: PWPRuleType[]
  status: string
  skp: PWPSkpType
  targeting_type: string
  segment_id: number
  segment_name: string
  [key: string]: unknown
}

export interface PWPPriorityType {
  code: string
  priority: number
  id: number
  skuNumber: string
  uniqueKey: string
  pwpProductId?: number
  [key: string]: string | number | undefined
}

interface PWPPrioritySearchProductStateType {
  data: (PWPPrioritySearchProductType & { name?: string })[]
  query: { search: string }
  selected: {
    code: string
    priority: number
    id: number
    skuNumber: string
    uniqueKey: string
    pwpProductId: number
  }
}

interface PromoPwpStateType {
  params: {
    search: string
    status: string
    startDate: number
    endDate: number
    sort: string
    direction: string
    pageSize: number
    pageIndex: number
  }
  pagination: {
    pageSize: number
    currentItem: number
    currentPage: number
    totalData: number
    last: boolean
  }
  dropdownStatus: { id: string; name: string }[]
  dropdownType: PWPDropdownType[]
  paramsProductList: {
    search: string
    selectedIds: string
    pageSize: number
    pageIndex: number
  }
  productList: PWPListProductType[]
  productListPagination: {
    pageSize: number
    currentItem: number
    currentPage: number
    totalData: number
    last: boolean
  }
  form: PWPFormType
  detail: PWPDetailType | null
  isLoading: boolean
  isLoadingForm: boolean
  isSubmitForm: boolean
  isLoadingProductList: boolean
  isLoadingBulkUpload: boolean
  isRuleActive: boolean
  isTargetedUser: boolean
  isShowModalTargetedUser: boolean
  isSkpLoadedOnEdit: boolean
  data: PWPListItemType[]
  targetedUserList: {
    data: PWPTargetedUserItemType[]
    query: { pageIndex: number; pageSize: number; totalElements: number; [key: string]: number }
  }
  isShowPWPPriority: boolean
  pwpPrioritySearchProduct: PWPPrioritySearchProductStateType
  pwpPriority: PWPPriorityType[]
  skps: Record<string, { id: number; name: string }>
  skpsSearch: Record<string, { id: number; name: string }>
}

export const INITIAL_RULES: PWPRuleType[] = [
  {
    key: 'category',
    items: [],
    is_exclude: false,
    isChecked: false,
  },
]

const initialState: PromoPwpStateType = {
  params: {
    search: '',
    status: '',
    startDate: 0,
    endDate: 0,
    sort: 'created_at',
    direction: 'DESC',
    pageSize: 10,
    pageIndex: 0,
  },
  pagination: {
    pageSize: 10,
    currentItem: 0,
    currentPage: 0,
    totalData: 0,
    last: false,
  },
  dropdownStatus: [
    { id: 'ACTIVE', name: 'Aktif' },
    { id: 'INACTIVE', name: 'Tidak Aktif' },
  ],
  dropdownType: [],
  paramsProductList: {
    search: '',
    selectedIds: '',
    pageSize: 20,
    pageIndex: 0,
  },
  productList: [],
  productListPagination: {
    pageSize: 10,
    currentItem: 0,
    currentPage: 0,
    totalData: 0,
    last: false,
  },
  form: {
    pwpName: '',
    startDate: '',
    startTime: '',
    endDate: '',
    endTime: '',
    isAllDay: false,
    limitCampaign: 0,
    limitPerDay: 0,
    minimumSpend: 0,
    description: '',
    skuRelated: [],
    products: [],
    productForCreatePwp: [],
    rules: INITIAL_RULES,
    status: 'ACTIVE',
    skp: {
      id: { id: 0, name: '' },
      name: '',
    },
    targeting_type: '',
    segment_id: 0,
    segment_name: '',
  },
  detail: null,
  isLoading: false,
  isLoadingForm: false,
  isSubmitForm: false,
  isLoadingProductList: false,
  isLoadingBulkUpload: false,
  isRuleActive: false,
  isTargetedUser: false,
  isShowModalTargetedUser: false,
  isSkpLoadedOnEdit: false,
  data: [],
  targetedUserList: {
    data: [],
    query: {
      pageIndex: 0,
      pageSize: 20,
      totalElements: 0,
    },
  },
  isShowPWPPriority: false,
  pwpPrioritySearchProduct: {
    data: [],
    query: {
      search: '',
    },
    selected: {
      code: '',
      priority: 0,
      id: 0,
      skuNumber: '',
      uniqueKey: '',
      pwpProductId: 0,
    },
  },
  pwpPriority: [],
  skps: {},
  skpsSearch: {},
}

const normalizeVoucherRulesData = (rules: PWPRuleDetailType[]): PWPRuleType[] =>
  rules.map((rule) => {
    if (rule.key === 'category') {
      const { items } = rule
      const transformedItems: CategoryType[] = items.map((item) => ({
        category_id: item.id,
        category_name: item.name,
      }))
      return { ...rule, items: transformedItems }
    }
    return undefined as unknown as PWPRuleType
  })

const promoPwp = createSlice({
  name: 'promoPwp',
  initialState,
  reducers: {
    setIsShowModalTargetedUser: (state, action: PayloadAction<boolean>) => {
      state.isShowModalTargetedUser = action.payload
    },
    setIsLoading: (state, action: PayloadAction<boolean>) => {
      state.isLoading = action.payload
    },
    setIsLoadingForm: (state, action: PayloadAction<boolean>) => {
      state.isLoadingForm = action.payload
    },
    setIsSubmitForm: (state, action: PayloadAction<boolean>) => {
      state.isSubmitForm = action.payload
    },
    setIsLoadingProductList: (state, action: PayloadAction<boolean>) => {
      state.isLoadingProductList = action.payload
    },
    setIsLoadingBulkUpload: (state, action: PayloadAction<boolean>) => {
      state.isLoadingBulkUpload = action.payload
    },
    setIsRuleActive: (state, action: PayloadAction<boolean>) => {
      state.isRuleActive = action.payload
    },
    setIsTargetedUser: (state, action: PayloadAction<boolean>) => {
      state.isTargetedUser = action.payload
    },
    setData: (state, action: PayloadAction<PWPListItemType[]>) => {
      state.data = action.payload
    },
    setDropdownType: (state, action: PayloadAction<PWPDropdownType[]>) => {
      state.dropdownType = action.payload
    },
    setPagination: (state, action: PayloadAction<PromoPwpStateType['pagination']>) => {
      state.pagination = action.payload
    },
    setDetail: (state, action: PayloadAction<PWPDetailType>) => {
      state.detail = action.payload
    },
    setProductListPagination: (
      state,
      action: PayloadAction<PromoPwpStateType['productListPagination']>,
    ) => {
      state.productListPagination = action.payload
    },
    setParamsProductList: (
      state,
      action: PayloadAction<PromoPwpStateType['paramsProductList']>,
    ) => {
      state.paramsProductList = action.payload
    },
    setParams: (state, action: PayloadAction<PromoPwpStateType['params']>) => {
      state.params = action.payload
    },
    setProductList: (state, action: PayloadAction<PWPListProductType[]>) => {
      state.productList = action.payload
    },
    setForm: (state, action: PayloadAction<Partial<PWPFormType>>) => {
      state.form = { ...state.form, ...action.payload }
    },
    setFormLocationHub: (
      state,
      action: PayloadAction<{
        indexHub: number
        indexProduct: number
        name: string
        value: string
      }>,
    ) => {
      const { indexHub, indexProduct, name, value } = action.payload
      const numValue = name === 'status' ? value : Number(value ?? 0)
      const newProductForCreatePwp = [...state.form.productForCreatePwp]
      ;(
        newProductForCreatePwp[indexProduct].pwpProductQuota[indexHub] as unknown as Record<
          string,
          string | number
        >
      )[name] = numValue
      state.form = {
        ...state.form,
        productForCreatePwp: newProductForCreatePwp,
      }
    },
    setFormProduct: (
      state,
      action: PayloadAction<{
        indexProduct: number
        name: string
        value: string | number
      }>,
    ) => {
      const { indexProduct, name, value } = action.payload
      const numValue = Number(value)
      const newProductForCreatePwp = [...state.form.productForCreatePwp]
      newProductForCreatePwp[indexProduct][name] = numValue
      state.form = {
        ...state.form,
        productForCreatePwp: newProductForCreatePwp,
      }
    },
    setFormProductForCreatePwp: (state) => {
      const newProducts = state.form.products.map((i) => {
        const selected = state.form.productForCreatePwp.find((s) => s.productId === i.productId)
        // eslint-disable-next-line no-unneeded-ternary
        return selected ? selected : i
      })

      state.form = {
        ...state.form,
        productForCreatePwp: [...newProducts],
      }
    },
    setFormRules: (state, action: PayloadAction<PWPRuleType>) => {
      state.form = {
        ...state.form,
        rules: [action.payload],
      }
    },
    setSkps: (state, action: PayloadAction<Record<string, { id: number; name: string }>>) => {
      state.skps = action.payload
    },
    setSkpsSearch: (state, action: PayloadAction<Record<string, { id: number; name: string }>>) => {
      state.skpsSearch = action.payload
    },
    setSkpValue: (state, action: PayloadAction<PWPSkpType>) => {
      state.form.skp = action.payload
    },
    setIsSkpLoadedOnEdit: (state, action: PayloadAction<boolean>) => {
      state.isSkpLoadedOnEdit = action.payload
    },
    resetStateForm: (state) => {
      Object.assign(state, {
        detail: initialState.detail,
        dropdownType: initialState.dropdownType,
        paramsProductList: initialState.paramsProductList,
        productList: initialState.productList,
        productListPagination: initialState.productListPagination,
        form: initialState.form,
        isLoadingProductList: initialState.isLoadingProductList,
        isLoadingBulkUpload: initialState.isLoadingBulkUpload,
        isLoadingForm: initialState.isLoadingForm,
        isSubmitForm: initialState.isSubmitForm,
        isRuleActive: initialState.isRuleActive,
      })
    },
    reset: (state) => {
      Object.assign(state, { ...initialState })
    },
    setQueryTargetedUser: (
      state,
      { payload: { name, value } }: PayloadAction<{ name: string; value: number }>,
    ) => {
      state.targetedUserList.query[name] = value
    },
    setIsShowPWPPriority: (state, { payload: { value } }: PayloadAction<{ value: boolean }>) => {
      state.isShowPWPPriority = value
    },
    setSelectedPWPPrioritySearchProduct: (
      state,
      {
        payload: { value },
      }: PayloadAction<{
        value: PWPPrioritySearchProductStateType['selected']
      }>,
    ) => {
      state.pwpPrioritySearchProduct.selected = value
    },
    addPWPPriority: (state) => {
      state.pwpPriority.push(state.pwpPrioritySearchProduct.selected)
      state.pwpPrioritySearchProduct.selected = initialState.pwpPrioritySearchProduct.selected
    },
    removePWPPriority: (
      state,
      { payload: { value } }: PayloadAction<{ value: { uniqueKey: string } }>,
    ) => {
      state.pwpPriority = state.pwpPriority.filter((el) => el.uniqueKey !== value.uniqueKey)
    },
    setPWPPriority: (
      state,
      { payload: { value } }: PayloadAction<{ value: PWPPriorityType[] }>,
    ) => {
      state.pwpPriority = value
    },
    resetPWPPriority: (state) => {
      state.pwpPriority = initialState.pwpPriority
      state.pwpPrioritySearchProduct = initialState.pwpPrioritySearchProduct
    },
  },
  extraReducers(builder) {
    builder
      .addCase(fetchTargetedUser.pending, (state) => {
        state.isLoading = true
      })
      .addCase(fetchTargetedUser.fulfilled, (state, action) => {
        const { data, pagination } = action.payload
        state.isLoading = false
        state.targetedUserList.query.totalElements = pagination.totalElements
        state.targetedUserList.data = data.list
      })
      .addCase(fetchTargetedUser.rejected, (state) => {
        state.isLoading = false
      })
      .addCase(removeTargetedUser.pending, (state) => {
        state.isLoading = true
      })
      .addCase(removeTargetedUser.fulfilled, (state) => {
        state.isLoading = false
      })
      .addCase(removeTargetedUser.rejected, (state) => {
        state.isLoading = false
      })
      .addCase(fetchSearchPWPProductTargeted.fulfilled, (state, action) => {
        state.pwpPrioritySearchProduct.data = action.payload.data.map((el) => ({
          ...el,
          name: el.fullName,
        }))
      })
      .addCase(fetchPWPPriority.pending, (state) => {
        state.isLoading = true
      })
      .addCase(fetchPWPPriority.fulfilled, (state, action) => {
        state.pwpPriority = action.payload.data.map((el) => ({
          code: el.title,
          priority: el.order,
          id: el.id,
          skuNumber: el.skuNumber ? el.skuNumber : '',
          uniqueKey: window.crypto.randomUUID(),
          pwpProductId: el.pwpProductId,
        }))
        state.isLoading = false
      })
      .addCase(fetchPWPPriority.rejected, (state) => {
        state.isLoading = false
      })
      .addCase(updatePWPPriority.pending, (state) => {
        state.isLoading = true
      })
      .addCase(updatePWPPriority.fulfilled, (state) => {
        state.isLoading = false
      })
      .addCase(updatePWPPriority.rejected, (state) => {
        state.isLoading = false
      })
  },
})

export const {
  reset,
  resetStateForm,
  setPagination,
  setIsLoading,
  setIsLoadingForm,
  setIsLoadingBulkUpload,
  setIsRuleActive,
  setFormRules,
  setData,
  setDropdownType,
  setForm,
  setParams,
  setProductList,
  setProductListPagination,
  setIsLoadingProductList,
  setParamsProductList,
  setFormLocationHub,
  setFormProduct,
  setFormProductForCreatePwp,
  setDetail,
  setIsSubmitForm,
  setIsTargetedUser,
  setIsShowModalTargetedUser,
  setQueryTargetedUser,
  setIsShowPWPPriority,
  setSelectedPWPPrioritySearchProduct,
  addPWPPriority,
  setPWPPriority,
  resetPWPPriority,
  removePWPPriority,
  setSkps,
  setSkpsSearch,
  setSkpValue,
  setIsSkpLoadedOnEdit,
} = promoPwp.actions

export const getData = createAsyncThunk(
  'promoPwp/getData',
  async (params: PWPListParamsType, { dispatch, rejectWithValue }) => {
    dispatch(setIsLoading(true))
    try {
      dispatch(setPagination(initialState.pagination))

      const {
        data: {
          data,
          pagination: { pageSize, pageIndex, totalPages, totalElements, numberOfElements },
        },
      } = await getPwpData(params)
      dispatch(setData(data.list))
      dispatch(
        setPagination({
          pageSize,
          currentItem: numberOfElements,
          currentPage: pageIndex,
          totalData: totalElements,
          last: pageIndex === totalPages,
        }),
      )
    } catch (error) {
      dispatch(setData(initialState.data))
      dispatch(setPagination(initialState.pagination))
      callErrorMsg(error, 'Gagal Load Data')
      return rejectWithValue(error)
    } finally {
      dispatch(setIsLoading(false))
    }
    return undefined
  },
)

export const getDetail = createAsyncThunk<void, { id: string | number; cb?: () => void }>(
  'promoPwp/getDetail',
  async ({ id, cb }, { dispatch, rejectWithValue, getState }) => {
    dispatch(setIsLoadingForm(true))
    try {
      const {
        data: {
          data: { pwp_detail },
        },
      } = await getPwpDetail(id)

      const { dropdownType } = (getState() as StoreStateType).promoPwp

      const rules =
        pwp_detail?.rules?.length > 0 ? normalizeVoucherRulesData(pwp_detail?.rules) : INITIAL_RULES

      dispatch(setDetail(pwp_detail))
      dispatch(
        setForm({
          pwpName: pwp_detail.campaign_name,
          startDate: pwp_detail.start_date,
          startTime: pwp_detail.start_time,
          endDate: pwp_detail.end_date,
          endTime: pwp_detail.end_time,
          isAllDay: pwp_detail.is_all_day,
          limitCampaign: pwp_detail.limit_campaign,
          limitPerDay: pwp_detail.limit_per_day,
          minimumSpend: pwp_detail.minimum_spend,
          description: pwp_detail.description,
          status: pwp_detail.status,
          targeting_type: pwp_detail.targeting_type,
          segment_id: pwp_detail.segment_id,
          segment_name: pwp_detail.segment_name,
          isNewUser: pwp_detail.is_new_user,
          productForCreatePwp: pwp_detail.pwp_products.map((p) => ({
            productId: p.product_id,
            productName: p.product_name,
            sku: p.sku,
            discount: p.discount,
            limit: p.limit,
            pwpProductQuota: p.pwp_product_quota.map((i) => ({
              locationId: i.location_hub_id,
              locationName: i.location_name,
              quotaCampaign: i.quota_campaign ?? 0,
              quotaPerDay: i.quota_per_day ?? 0,
              stock: i.stock,
              status: i.status === 'ACTIVE' ? 'ACTIVE' : 'INACTIVE',
              price: i.price,
            })),
          })),
          products: pwp_detail.pwp_products.map((p) => ({
            productId: p.product_id,
            productName: p.product_name,
            sku: p.sku,
            discount: p.discount,
            limit: p.limit,
            pwpProductQuota: p.pwp_product_quota.map((i) => ({
              locationId: i.location_hub_id,
              locationName: i.location_name,
              quotaCampaign: i.quota_campaign ?? 0,
              quotaPerDay: i.quota_per_day ?? 0,
              stock: 0,
              status: !i.status ? 'INACTIVE' : 'ACTIVE',
              price: i.price,
            })),
          })),
          rules,
          skp: {
            id: { id: pwp_detail.skp.skp_id, name: pwp_detail.skp.skp_name },
            name: pwp_detail.skp.skp_name,
          },
        }),
      )

      dispatch(setIsRuleActive(pwp_detail?.rules?.length > 0))
      dispatch(setIsTargetedUser(pwp_detail?.is_targeted))

      dispatch(
        setDropdownType([
          ...dropdownType.map((i) => ({
            ...i,
            // eslint-disable-next-line no-unneeded-ternary
            checked:
              // eslint-disable-next-line no-unneeded-ternary
              (i.key === enumPwpRules.CUSTOMER_BARU && pwp_detail.is_new_user) ||
              (i.key === enumPwpRules.MINIMUM_SPEND && pwp_detail.minimum_spend > 0)
                ? true
                : false,
          })),
        ]),
      )
    } catch (error) {
      callErrorMsg(error, 'Gagal Load Data Detail')
      dispatch(resetStateForm())
      if (cb) cb()
      return rejectWithValue(error)
    } finally {
      dispatch(setIsLoadingForm(false))
    }
    return undefined
  },
)

export const getType = createAsyncThunk(
  'promoPwp/getType',
  async (_, { dispatch, rejectWithValue, getState }) => {
    try {
      const { detail } = (getState() as StoreStateType).promoPwp
      const {
        data: { data },
      } = await getPwpType()

      const filteredData = data.filter((e: { value: string }) => e.value !== 'CUSTOMER_BARU')
      dispatch(
        setDropdownType(
          filteredData.map((i: { value: string; label: string }) => ({
            checked:
              i.value === enumPwpRules.CUSTOMER_BARU && detail
                ? (detail as unknown as { isNewUser: boolean }).isNewUser
                : false,
            childs: [],
            key: i.value,
            name: i.label,
          })),
        ),
      )
      return null
    } catch (error) {
      dispatch(setDropdownType(initialState.dropdownType))
      callErrorMsg(error, 'Gagal Load Data Type')
      return rejectWithValue(error)
    }
  },
)

export const getProduct = createAsyncThunk(
  'promoPwp/getProduct',
  async (params: PWPProductListParamsType, { dispatch, rejectWithValue }) => {
    dispatch(setIsLoadingProductList(true))
    try {
      const {
        data: { data },
      } = await getPwpProduct(params)

      const {
        numberOfElements,
        totalElements,
        totalPages,
        size: pageSize,
        number: pageIndex,
        content,
      } = data

      dispatch(setProductList(content))
      dispatch(
        setProductListPagination({
          pageSize,
          currentItem: numberOfElements,
          currentPage: pageIndex,
          totalData: totalElements,
          last: pageIndex === totalPages,
        }),
      )
    } catch (error) {
      dispatch(setProductList(initialState.productList))
      dispatch(setProductListPagination(initialState.productListPagination))
      callErrorMsg(error, 'Gagal Load Data Product')
      return rejectWithValue(error)
    } finally {
      dispatch(setIsLoadingProductList(false))
    }
    return undefined
  },
)

export const bulkUpload = createAsyncThunk(
  'promoPwp/bulkUpload',
  async (params: FormData, { dispatch, rejectWithValue, getState }) => {
    dispatch(setIsLoadingBulkUpload(true))
    const { form } = (getState() as StoreStateType).promoPwp
    try {
      const {
        data: {
          data: { content },
        },
      } = await postPwpBulkUpload(params)

      dispatch(
        setForm({
          ...form,
          products: [],
          productForCreatePwp: [],
        }),
      )

      setTimeout(() => {
        const products = content.map((product) => ({
          ...product,
          pwpProductQuota: [...product.details],
        }))

        dispatch(
          setForm({
            ...form,
            products: products as unknown as PWPProductType[],
            productForCreatePwp: products as unknown as PWPProductType[],
          }),
        )
      }, 500)
    } catch (error) {
      dispatch(setForm({ ...form, products: [], productForCreatePwp: [] }))
      callErrorMsg(error, 'Gagal melakukan Upload Sekaligus')
      return rejectWithValue(error)
    } finally {
      dispatch(setIsLoadingBulkUpload(false))
    }
    return undefined
  },
)

export const createPwp = createAsyncThunk<
  void,
  { payload: import('utils/apiList/promoPwp').PWPCreatePayloadType; cb?: () => void }
>('promoPwp/createPwp', async ({ payload, cb }, { dispatch, rejectWithValue, getState }) => {
  dispatch(setIsLoadingForm(true))
  dispatch(setIsSubmitForm(true))
  const { form } = (getState() as StoreStateType).promoPwp
  try {
    const err = checkFormValidation(form)
    if (err) throw { response: { data: { message: err } } }
    const { data } = await postPwpCreate(payload)

    if (data?.error?.status) {
      toastFailed(data?.error?.message)
    } else {
      toastSuccess('Berhasil melakukan tambah data PWP')
      dispatch(resetStateForm())
      if (cb) cb()
    }
  } catch (error) {
    callErrorMsg(error, 'Gagal melakukan tambah data PWP')
    return rejectWithValue(error)
  } finally {
    dispatch(setIsLoadingForm(false))
  }
  return undefined
})

export const getCommercialInternalSkp = createAsyncThunk(
  'promoPwp/getCommercialInternalSkp',
  async (params: PWPSkpParamsType = {}, { dispatch, rejectWithValue, getState }) => {
    try {
      const {
        data: { data },
      } = await getPwpCommercialInternalSkp(params)

      const { skps } = (getState() as StoreStateType).promoPwp

      const newSkpsUnlisted: Record<string, { id: number; name: string }> = {}
      const skpSearch: Record<string, { id: number; name: string }> = {}

      data.forEach((el) => {
        const value = { id: el.id, name: el.skp_no }
        if (!skps[el.skp_no]) {
          newSkpsUnlisted[el.skp_no] = value
        }
        if (params.skp_no !== '') {
          skpSearch[el.skp_no] = value
        }
      })

      dispatch(setSkps({ ...newSkpsUnlisted, ...skps }))
      dispatch(setSkpsSearch(skpSearch))
      return null
    } catch (error) {
      callErrorMsg(error, 'Gagal Load Data Type')
      return rejectWithValue(error)
    }
  },
)

export const getDetailCommercialInternalSkp = createAsyncThunk(
  'promoPwp/getDetailCommercialInternalSkp',
  async (params: PWPSkpParamsType = {}, { dispatch, rejectWithValue, getState }) => {
    try {
      const {
        skps,
        form: { skp },
      } = (getState() as StoreStateType).promoPwp

      const entriesSksp = Object.entries(skps).find((el) => el[1].id === skp.id.id)

      if (!entriesSksp) {
        const {
          data: { data },
        } = await getPwpCommercialInternalSkp({
          ...params,
          campaign_name: skp.name,
          status: 'Approved',
        })

        const getDetailDataSelected = data.find((el) => el.id === skp.id.id)

        if (getDetailDataSelected) {
          dispatch(
            setSkps({
              [getDetailDataSelected.skp_no]: {
                id: getDetailDataSelected.id,
                name: getDetailDataSelected.skp_no,
              },
              ...skps,
            }),
          )
        }
      }
      dispatch(setIsSkpLoadedOnEdit(true))
      return null
    } catch (error) {
      callErrorMsg(error, 'Gagal Load Data Type')
      return rejectWithValue(error)
    }
  },
)

export const getMinPriceProductPwpOnActiveHubs = (productPwp: PWPProductType): number => {
  const productOnActivedHubs = [...productPwp.pwpProductQuota].filter(
    (i) => i?.status === enumProductOnHubStatus.ACTIVE,
  )

  return Math.min(...productOnActivedHubs.map((i) => i.price))
}

const checkFormValidation = (form: PWPFormType): React.ReactNode => {
  const errMessage: Record<string, string> = {
    discount100Percent: '',
    discountGtPrice: '',
    limitGtQuotaCampaign: '',
    limitGtQuota: '',
    quotaPerDayGtQuotaCampaign: '',
    quotaPerDayGtStockHub: '',
    quotaCampaignGtStockHub: '',
    limitPerDayGtLimitCampaign: '',
  }

  if (form.limitPerDay > form.limitCampaign) {
    errMessage.limitPerDayGtLimitCampaign =
      'Limit redeem per hari tidak boleh lebih besar dari limit redeem per campaign.'
  }

  form.productForCreatePwp.forEach((p) => {
    if (p.limit > 0 && p.limit > form.limitCampaign) {
      errMessage.limitPerDayGtLimitCampaign =
        'Limit penukaran produk tidak boleh lebih besar dari limit redeem per campaign.'
    }

    const minPrice = getMinPriceProductPwpOnActiveHubs(p)

    if (p.discount > 0) {
      if (minPrice < p.discount) {
        errMessage.discountGtPrice = 'Harga diskon tidak boleh melebihi harga normal'
      }
      if (minPrice === p.discount) {
        errMessage.discount100Percent = 'Harga diskon tidak boleh 100%'
      }
    }

    p.pwpProductQuota.forEach((q) => {
      if (q.quotaPerDay > 0 && q.quotaPerDay > q.quotaCampaign) {
        errMessage.quotaPerDayGtQuotaCampaign = 'Quota per hari melebihi quota campaign'
      }
    })
  })

  const messages = Object.keys(errMessage).filter((i) => errMessage[i])

  return messages.length > 0 ? (
    <ol style={{ margin: 10 }}>
      {messages.map((i, idx) => (
        <li key={`errMessagePwpForm-${idx}`}>{errMessage[i]}</li>
      ))}
    </ol>
  ) : null
}

export const updatePwp = createAsyncThunk<
  void,
  {
    id: string | number
    payload: import('utils/apiList/promoPwp').PWPCreatePayloadType
    cb?: () => void
  }
>('promoPwp/updatePwp', async ({ id, payload, cb }, { dispatch, rejectWithValue, getState }) => {
  dispatch(setIsLoadingForm(true))
  dispatch(setIsSubmitForm(true))

  const { form } = (getState() as StoreStateType).promoPwp

  try {
    const err = checkFormValidation(form)
    if (err) throw { response: { data: { message: err } } }
    const { data } = await putPwpEdit(id, payload)

    if (data?.error?.status) {
      toastFailed(data?.error?.message)
    } else {
      toastSuccess('Berhasil melakukan edit data PWP')
      dispatch(resetStateForm())
      if (cb) cb()
    }
  } catch (error) {
    callErrorMsg(error, 'Gagal melakukan edit data PWP')
    return rejectWithValue(error)
  } finally {
    dispatch(setIsLoadingForm(false))
  }
  return undefined
})

export const updateStatusPwp = createAsyncThunk<
  void,
  { id: string | number; payload: import('utils/apiList/promoPwp').PWPUpdateStatusPayloadType }
>('promoPwp/updateStatusPwp', async ({ id, payload }, { dispatch, rejectWithValue, getState }) => {
  dispatch(setIsLoadingForm(true))
  const { params } = (getState() as StoreStateType).promoPwp
  try {
    await putPwpEditStatus(id, payload)
    toastSuccess('Berhasil melakukan update status PWP')
    dispatch(getData(params))
  } catch (err) {
    callErrorMsg(err, 'Gagal melakukan update status PWP')
    return rejectWithValue(err)
  } finally {
    dispatch(setIsLoadingForm(false))
  }
  return undefined
})

export default promoPwp.reducer
